/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { BoltIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { getAnomalies, getOverview, muteProblem, unmuteProblem } from "@/api/overview";
import { CheckBadge } from "@/components/common/CheckBadge";
import { MuteButton } from "@/components/homepage/MuteButton";
import { cn } from "@/lib/utils";

/** Open problems and anomalies (the list that used to be a card on Home), with mute / unmute. */
export const ProblemsPanel: React.FC<{ focus: string | null }> = ({ focus }) => {
  const queryClient = useQueryClient();
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: 30_000 });
  const { data: anomalies = [] } = useQuery({ queryKey: ["anomalies"], queryFn: getAnomalies, refetchInterval: 30_000 });
  const problems = overview?.problems ?? [];
  const muted = overview?.muted ?? [];
  const [showMuted, setShowMuted] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["overview"] });

  // a deep link or a notification points at one item: bring it into view
  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    focusRef.current?.scrollIntoView({ block: "center" });
  }, [focus, problems.length]);

  if (problems.length + anomalies.length === 0 && muted.length === 0) {
    return (
      <div className="cc-empty nc-empty">
        <CheckBadge size={40} />
        Nothing needs your attention
      </div>
    );
  }

  return (
    <div className="nc-problems">
      {problems.length + anomalies.length === 0 ? (
        <div className="cc-empty nc-empty">
          <CheckBadge size={40} />
          Nothing needs your attention
        </div>
      ) : (
        <ul className="cc-plist">
          <AnimatePresence initial={false}>
            {problems.map((p) => {
              const isFocus = focus !== null && (focus === p.name || focus === `${p.type}:${p.name}`);
              return (
                <motion.li
                  key={`${p.type}-${p.name}`}
                  ref={isFocus ? focusRef : undefined}
                  className={cn(isFocus && "nc-focus")}
                  layout="position"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0.12 } }}
                  transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
                >
                  {p.severity === "crit" ? (
                    <XCircleIcon className="h-4 w-4 flex-none text-[var(--gh-crit)]" aria-label="critical" />
                  ) : (
                    <ExclamationTriangleIcon className="h-4 w-4 flex-none text-[var(--gh-warn)]" aria-label="warning" />
                  )}
                  <b>{p.name}</b>
                  <em>{p.type}</em>
                  <code>{p.reason}</code>
                  <MuteButton
                    onMute={(seconds) => muteProblem({ type: p.type, name: p.name, reason: p.reason, seconds }).then(refresh)}
                  />
                </motion.li>
              );
            })}
          </AnimatePresence>
          {anomalies.map((a, i) => (
            <li key={`${a.monitor}-${a.metric}-${i}`}>
              <BoltIcon className="h-4 w-4 flex-none text-[var(--gh-warn)]" aria-label="anomaly" />
              <b>{a.monitor}</b>
              <em>anomaly</em>
              <code>
                {a.metric} {a.current}
                {a.unit} vs {a.baseline}
                {a.unit}
              </code>
            </li>
          ))}
        </ul>
      )}
      {muted.length > 0 && (
        <div className="cc-muted">
          <button type="button" className="cc-muted-toggle" onClick={() => setShowMuted((v) => !v)}>
            {`Muted (${muted.length})`}
          </button>
          {showMuted && (
            <ul>
              {muted.map((m) => (
                <li key={`${m.type}-${m.name}`}>
                  <b>{m.name}</b>
                  <em>{m.until === 0 ? "forever" : `until ${new Date(m.until * 1000).toLocaleDateString()}`}</em>
                  <button type="button" onClick={() => unmuteProblem({ type: m.type, name: m.name }).then(refresh)}>
                    Unmute
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
